import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FileText, FileSpreadsheet, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CellPicker, stamp } from "@/components/attendance/AttendanceEditors";
import SessionDialog from "@/components/attendance/SessionDialog";
import MemberDeepDive from "@/components/attendance/MemberDeepDive";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/PageHeader";
import api, { formatApiError } from "@/lib/api";
import { formatDateOnly, formatDateTime } from "@/lib/date";
import { TapInfo } from "@/components/TapRow";

const takenAt = (e) => (e.submitted_at ? `${formatDateTime(e.submitted_at, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}${e.updated ? " (updated)" : ""}` : "");

function Cell({ status, testId }) {
  const base = "inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold";
  if (status === "present") return <span data-testid={testId} className={`${base} bg-emerald-100 text-emerald-700`}>P</span>;
  if (status === "absent") return <span data-testid={testId} className={`${base} bg-red-100 text-red-700`}>A</span>;
  if (status === "tardy") return <span data-testid={testId} className={`${base} bg-amber-100 text-amber-700`}>T</span>;
  if (status === "excused") return <span data-testid={testId} className={`${base} bg-sky-100 text-sky-700`}>E</span>;
  return <span data-testid={testId} className="text-slate-300">·</span>;
}

const PAGE_SIZE = 10;

const dayNum = (s) => { const [y, m, d] = s.slice(0, 10).split("-").map(Number); return Date.UTC(y, m - 1, d) / 86400000; };

// Index of the event on `date`, else the closest one either side (ties go to the earlier rehearsal).
function closestIndex(events, date) {
  const target = dayNum(date);
  let best = 0;
  events.forEach((e, i) => { if (Math.abs(dayNum(e.event_date) - target) < Math.abs(dayNum(events[best].event_date) - target)) best = i; });
  return best;
}

function Pager({ page, pages, shown, total, first, last, setPage, jumpDate, onJump, note }) {
  return (
    <div className="mb-3 space-y-2" data-testid="attendance-pager">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-sm text-slate-500" data-testid="attendance-page-label">
        Showing <span className="font-semibold text-slate-700">{shown}</span> of {total} rehearsal{total === 1 ? "" : "s"} · {formatDateOnly(first)} – {formatDateOnly(last)}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-xs font-medium text-slate-500">
          Jump to date
          <Input type="date" value={jumpDate} onChange={(e) => onJump(e.target.value)} className="h-8 w-40" data-testid="attendance-jump-date" />
        </label>
        <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="rounded-full" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="attendance-page-newer"><ChevronLeft className="mr-1 h-4 w-4" /> Newer</Button>
        <span className="whitespace-nowrap text-xs text-slate-400 tabular-nums" data-testid="attendance-page-indicator">Page {page + 1} of {pages}</span>
        <Button variant="outline" size="sm" className="rounded-full" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)} data-testid="attendance-page-older">Older <ChevronRight className="ml-1 h-4 w-4" /></Button>
        </div>
      </div>
    </div>
    {note && <p className="rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700" data-testid="attendance-jump-note">{note}</p>}
    </div>
  );
}

export default function AttendanceGrid() {
  const [events, setEvents] = useState([]);
  const [rows, setRows] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [memberFilter, setMemberFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [exporting, setExporting] = useState(null);
  const [page, setPage] = useState(0);
  const [jumpDate, setJumpDate] = useState("");
  const [jumpNote, setJumpNote] = useState("");
  const [highlightId, setHighlightId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [deepId, setDeepId] = useState(null);
  const { viewOnly } = useAuth();
  const canEdit = !viewOnly;

  // Save one member's status on one session, then patch local state (keeps the current page).
  const saveRecord = async (eventId, memberId, status, note) => {
    try {
      const { data } = await api.patch(`/attendance/event/${eventId}/member/${memberId}`, { status, note });
      setRows((rs) => rs.map((r) => (r.member_id !== memberId ? r
        : { ...r, cells: { ...r.cells, [eventId]: data.status }, notes: { ...r.notes, [eventId]: data.note } })));
      setEvents((es) => es.map((e) => (e.id === eventId ? { ...e, edited_by_name: data.edited_by_name, edited_at: data.edited_at } : e)));
      toast.success("Attendance updated");
      return true;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); return false; }
  };

  const download = async (fmt) => {
    setExporting(fmt);
    try {
      const params = { fmt };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (memberFilter !== "all") params.member_id = memberFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await api.get("/attendance/grid/export", { params, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      const span = fromDate || toDate ? `${fromDate || "start"}_to_${toDate || "today"}` : "all-dates";
      a.download = `attendance-${span}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Unable to export attendance"); }
    finally { setExporting(null); }
  };

  const load = useCallback(async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const { data } = await api.get("/attendance/grid", { params });
      setEvents(data.events || []);
      setRows(data.rows || []);
      setPage(0); setJumpDate(""); setJumpNote("");
    } catch { /* ignore */ }
  }, [fromDate, toDate]);
  useEffect(() => { load(); }, [load]);

  const visibleRows = useMemo(() => {
    let rs = memberFilter === "all" ? rows : rows.filter((r) => r.member_id === memberFilter);
    if (statusFilter !== "all") rs = rs.filter((r) => Object.values(r.cells || {}).includes(statusFilter));
    return rs;
  }, [rows, memberFilter, statusFilter]);

  // Page 0 = the newest PAGE_SIZE events; columns run newest → oldest (scroll right for older).
  const pages = Math.max(1, Math.ceil(events.length / PAGE_SIZE));
  const pageEvents = useMemo(() => {
    const end = events.length - page * PAGE_SIZE;
    return events.slice(Math.max(0, end - PAGE_SIZE), Math.max(0, end)).reverse();
  }, [events, page]);

  const goPage = (fn) => { setJumpNote(""); setJumpDate(""); setPage(fn); };

  const jumpTo = (date) => {
    setJumpDate(date);
    if (!date || !events.length) { setJumpNote(""); return; }
    const i = closestIndex(events, date);
    const ev = events[i];
    setPage(Math.floor((events.length - 1 - i) / PAGE_SIZE));
    setHighlightId(ev.id);
    setJumpNote(ev.event_date.slice(0, 10) === date ? "" : `No rehearsal on ${formatDateOnly(date)} — showing closest: ${formatDateOnly(ev.event_date)}`);
  };

  // Scroll the jumped-to column into view, then fade the highlight.
  useEffect(() => {
    if (!highlightId) return undefined;
    document.querySelector(`[data-testid="attendance-col-${highlightId}"]`)?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    const t = setTimeout(() => setHighlightId(null), 2500);
    return () => clearTimeout(t);
  }, [highlightId, page]);

  const cellFor = (row, evId) => {
    const s = row.cells?.[evId];
    if (statusFilter !== "all" && s !== statusFilter) return null;
    return s || null;
  };

  return (
    <div data-testid="attendance-grid-page">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Every member's attendance across rehearsals and events.</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-full" disabled={!events.length || !!exporting} onClick={() => download("csv")} data-testid="attendance-export-csv-btn">
            {exporting === "csv" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />} CSV
          </Button>
          <Button className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={!events.length || !!exporting} onClick={() => download("pdf")} data-testid="attendance-export-pdf-btn">
            {exporting === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />} Download PDF
          </Button>
        </div>
      </div>

      <Card className="mb-4 flex flex-wrap items-end gap-4 p-4">
        <div><Label className="text-xs text-slate-500">From</Label><Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40" data-testid="attendance-from-date" /></div>
        <div><Label className="text-xs text-slate-500">To</Label><Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40" data-testid="attendance-to-date" /></div>
        <div className="min-w-[200px]">
          <Label className="text-xs text-slate-500">Team member</Label>
          <Select value={memberFilter} onValueChange={setMemberFilter}>
            <SelectTrigger data-testid="attendance-member-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="attendance-member-all">All members</SelectItem>
              {rows.map((r) => <SelectItem key={r.member_id} value={r.member_id} data-testid={`attendance-member-${r.member_id}`}>{r.member_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[160px]">
          <Label className="text-xs text-slate-500">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="attendance-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="attendance-status-all">All statuses</SelectItem>
              <SelectItem value="present" data-testid="attendance-status-present">Present</SelectItem>
              <SelectItem value="absent" data-testid="attendance-status-absent">Absent</SelectItem>
              <SelectItem value="tardy" data-testid="attendance-status-tardy">Tardy</SelectItem>
              <SelectItem value="excused" data-testid="attendance-status-excused">Excused</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex items-center gap-x-4 gap-y-1 pt-4 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><Cell status="present" /> Present</span>
          <span className="inline-flex items-center gap-1"><Cell status="absent" /> Absent</span>
          <span className="inline-flex items-center gap-1"><Cell status="tardy" /> Tardy</span>
          <span className="inline-flex items-center gap-1"><Cell status="excused" /> Excused</span>
        </div>
      </Card>

      {events.length === 0 ? (
        <EmptyState title="No attendance yet" description="Take attendance from a Daily Schedule event to populate this grid." testId="attendance-grid-empty" />
      ) : (
        <>
        <Pager page={page} pages={pages} shown={pageEvents.length} total={events.length}
          first={pageEvents[pageEvents.length - 1]?.event_date} last={pageEvents[0]?.event_date} setPage={goPage}
          jumpDate={jumpDate} onJump={jumpTo} note={jumpNote} />
        <Card className="overflow-x-auto border-slate-200 shadow-sm">
          <table className="min-w-full border-separate border-spacing-0" data-testid="attendance-grid-table">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                  <span className="inline-flex items-center gap-2">
                    <TapInfo testId="attendance-grid-tap-info"
                      tip={`Tap a member's row to open their attendance history, or a date to open that session.${canEdit ? " Tap a cell to change a status." : ""}`} />
                    Member
                  </span>
                </th>
                {pageEvents.map((e) => (
                  <th key={e.id} onClick={() => setSessionId(e.id)} title={[e.title, e.taken_by_name && `Taken by ${e.taken_by_name} · ${takenAt(e)}`, e.edited_by_name && `Edited by ${e.edited_by_name} · ${stamp(e.edited_at)}`, "Tap to open this session"].filter(Boolean).join("\n")} className={`cursor-pointer whitespace-nowrap px-3 py-3 text-center text-xs font-bold text-slate-500 transition-colors duration-500 hover:bg-slate-100 ${highlightId === e.id ? "bg-orange-100 ring-2 ring-inset ring-orange-400" : "bg-slate-50"}`} data-highlighted={highlightId === e.id ? "true" : undefined} data-testid={`attendance-col-${e.id}`}>
                    <span className="block tabular-nums underline decoration-slate-300 decoration-dotted underline-offset-2">{formatDateOnly(e.event_date)}</span>
                    <span className="mx-auto block max-w-[8rem] truncate text-[11px] font-medium normal-case text-slate-400" data-testid={`attendance-col-title-${e.id}`}>{e.title}</span>
                    {e.taken_by_name && <span className="mx-auto mt-1 block max-w-[8rem] truncate text-[10px] font-medium normal-case text-slate-500" data-testid={`attendance-col-takenby-${e.id}`}>Taken by {e.taken_by_name}</span>}
                    {e.submitted_at && <span className="block text-[10px] font-normal normal-case text-slate-400" data-testid={`attendance-col-submitted-${e.id}`}>{takenAt(e)}</span>}
                    {e.edited_by_name && <span className="mx-auto block max-w-[8rem] truncate text-[10px] font-semibold normal-case text-amber-600" data-testid={`attendance-col-editedby-${e.id}`}>Edited by {e.edited_by_name}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.member_id} onClick={() => setDeepId(row.member_id)} className="group cursor-pointer border-t border-slate-100" data-testid={`attendance-grid-row-${row.member_id}`}>
                  <td className="sticky left-0 z-10 whitespace-nowrap border-t border-slate-100 bg-white px-4 py-2 text-sm font-medium text-slate-800 transition-colors group-hover:bg-slate-50" data-testid={`attendance-row-name-${row.member_id}`}>{row.member_name}</td>
                  {pageEvents.map((e) => (
                    <td key={e.id} className={`border-t border-slate-100 px-3 py-2 text-center transition-colors duration-500 group-hover:bg-slate-50 ${highlightId === e.id ? "bg-orange-50" : ""}`}>
                      {canEdit
                        ? <CellPicker row={row} event={e} onSave={saveRecord} testId={`attendance-cell-${row.member_id}-${e.id}`}><Cell status={cellFor(row, e.id)} testId={`attendance-cell-${row.member_id}-${e.id}`} /></CellPicker>
                        : <Cell status={cellFor(row, e.id)} testId={`attendance-cell-${row.member_id}-${e.id}`} />}
                    </td>
                  ))}
                </tr>
              ))}
              {visibleRows.length === 0 && (
                <tr><td colSpan={pageEvents.length + 1} className="px-4 py-8 text-center text-sm text-slate-400" data-testid="attendance-grid-no-rows">No members match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </Card>
        </>
      )}
      {sessionId && <SessionDialog event={events.find((e) => e.id === sessionId)} rows={rows} canEdit={canEdit} onSave={saveRecord} onClose={() => setSessionId(null)} />}
      {deepId && <MemberDeepDive row={rows.find((r) => r.member_id === deepId)} events={events} canEdit={canEdit} onSave={saveRecord} onClose={() => setDeepId(null)}
        rangeLabel={fromDate || toDate ? `${fromDate ? formatDateOnly(fromDate) : "Start"} – ${toDate ? formatDateOnly(toDate) : "Today"}` : "All time"} />}
    </div>
  );
}
